import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { List } from 'immutable';
import { debounce } from 'underscore';

import prepareDetail from '../target/entitiesApiPrepape';
import connectOptions, { mergeProps } from '../../../utils/connectOptions';
import {
  handleUpdateCompany,
  startEditDetailEntity,
  saveInfo,
  closeValidationError,
  saveCompanyDetailEntity,
  saveBIC,
  revertChanges,
  findComparables,
} from '../../../actions/company/buyer/criteria';
import {
  getBuyerProjectsAddon,
  getBuyerProjectsPlatform,
  getStrategicBuyerProjects,
} from '../../../actions/company/buyer/projects';
import { getChanged, isDeepChanged, toJS, getIn } from '../../../utils/ChangeSpy';
import { Criteria } from '../../../components/Company/CompanyBuyer/Criteria';
import { PrettyErrorProvider } from '../../../contexts/PrettyErrorContext';
import { loadIndustries, updateIndustryTags, saveIndustries } from '../../../actions/project/industry';
import { loadTemplates } from '../../../actions/project/template';
import {
  loadProject,
  updateProjectInfo,
  saveProject,
  revertProjectChanges,
  updateProjectCommonInfo,
  loadProjectsComparables,
  saveProjectsComparables,
  clearComparables,
  changeComparables,
  loadProjectCriteria,
  saveProjectCriteria,
} from '../../../actions/project/main';
import { TYPE_FINANCIAL, TYPE_STRATEGIC } from '../../../config/constants/common';
import AutoCompleteContainer from '../../AutoComplete';
import { showError } from '../../../utils/MessagePopup';
import { BIC, BUSINESS_MODEL_ERROR, INDUSTRY_CATEGORY_ERROR } from '../../../utils/industryTagsHelper';
import { usePopupContext } from '../../../contexts/PopupContext';
import { useEntityEventContext } from '../../../contexts/EntityEventContext';

const CriteriaContainer = props => {
  const {
    addOnProjects,
    changeComparables,
    closeValidationError,
    common,
    comparables,
    criteria,
    findComparables,
    handleUpdateCompany,
    industries,
    industryLoading,
    inputErrors,
    isDuplicateCompany,
    loadIndustries,
    loading,
    loadProject,
    loadProjectCriteria,
    loadProjectsComparables,
    loadTemplates,
    match,
    projectData,
    projects,
    saveBIC,
    saveCompanyDetailEntity,
    saveIndustries,
    saveInfo,
    saveProject,
    saveProjectCriteria,
    saveProjectsComparables,
    startEditDetailEntity,
    type,
    updateIndustryTags,
    updateProjectCommonInfo,
    updateProjectInfo,
    getBuyerProjectsPlatform,
    getStrategicBuyerProjects,
    revertChanges,
    revertProjectChanges,
    clearComparables,
    user,
  } = props;

  const { openPopup } = usePopupContext();
  const {
    addOnSaveCallback,
    removeOnSaveCallback,
    addValidateCallBacks,
    deleteValidateCallbacks,
  } = useEntityEventContext();

  const [industryCategories, setIndustryCategories] = useState('');
  const [businessModels, setBusinessModels] = useState('');
  const [comparableValue, setComparableValue] = useState(['', '', '', '', '']);
  const [comparablesLoadable, setComparablesLoadable] = useState(true);
  const [comparablesData, setComparablesData] = useState([{}, {}, {}, {}, {}]);
  const [comparablesIds, setComparablesIds] = useState(['', '', '', '', '']);
  const [canLoadComparables, setCanLoadComparables] = useState(false);
  const [projectId, setProjectId] = useState(null);

  const { companyId } = match.params;

  // Bundles every value the mount-once validate/save callbacks (registered below) read live,
  // since a [] effect only ever sees the closures that existed at mount - see [[feedback-minimize-useref]].
  const liveRef = useRef({
    common,
    projectData,
    criteria,
    match,
    openPopup,
    projectId,
    comparablesIds,
    comparableValue,
  });

  liveRef.current = { common, projectData, criteria, match, openPopup, projectId, comparablesIds, comparableValue };

  const canEditData = !isDuplicateCompany;
  const errorMessage = `${industryCategories} ${businessModels}`.trim();

  const handleChange = useCallback(
    ({ name, value }) => {
      if (name === 'details.name') {
        updateProjectInfo('details', value);
      } else {
        handleUpdateCompany({ name, value });
      }
    },
    [updateProjectInfo, handleUpdateCompany],
  );

  const handleChangeCommon = useCallback(
    ({ name, value }) => {
      updateProjectCommonInfo(name, value);
    },
    [updateProjectCommonInfo],
  );

  const validate = useCallback(() => {
    let isValid = true;
    const criteriaValue = liveRef.current.criteria;
    const commonValue = liveRef.current.common;
    const projectDataValue = liveRef.current.projectData;
    const bic = criteriaValue.get(BIC);

    if (toJS(projectDataValue).category.name && bic.size) {
      const industryCategoriesValue = getIn(commonValue, 'selectedBICs');
      const businessModelsValue = getIn(commonValue, 'selectedBMs');

      if (!businessModelsValue.size) {
        setBusinessModels(BUSINESS_MODEL_ERROR);
        isValid = false;
      }

      if (!industryCategoriesValue.size) {
        setIndustryCategories(INDUSTRY_CATEGORY_ERROR);
        isValid = false;
      }
    }

    return isValid;
  }, []);

  const handleSubmit = useCallback(event => {
    event.preventDefault();

    const commonValue = liveRef.current.common;
    const projectDataValue = liveRef.current.projectData;
    const criteriaValue = liveRef.current.criteria;
    const matchValue = liveRef.current.match;

    if (criteriaValue.get('isValid')) {
      const body = getChanged(criteriaValue);
      const commonBody = getChanged(commonValue);
      const bic = criteriaValue.get(BIC);
      const { companyId: criteriaCompanyId } = matchValue.params;

      delete body.businessModels;
      delete body.industryCategories;

      if (Object.keys(commonBody).length) {
        saveProjectCriteria(criteriaCompanyId);
      }

      if (Object.keys(body).length) {
        saveInfo({
          companyId: criteriaCompanyId,
          body,
          afterSuccess: () => loadProjectCriteria(criteriaCompanyId, projectDataValue.get('id')),
        });
      }

      if (toJS(projectDataValue).category.name) {
        const tags = getIn(commonValue, 'selectedBICs');
        const models = getIn(commonValue, 'selectedBMs');

        if (tags.size === 0 || models.size === 0) {
          showError(liveRef.current.openPopup, ['Industry tags or Business models are missing']);
        } else {
          saveBIC({
            companyId: criteriaCompanyId,
            data: bic
              .filter(b => b.get('industryCategoryId') > 0)
              .map(b => ({
                industryCategoryId: getIn(b, 'industryCategoryId', ''),
                contactId: getIn(b, 'contactId') || null,
                notes: getIn(b, 'notes') || '',
                priority: 0,
              }))
              .toJS(),
            afterSuccess: () => {
              saveProject();
              saveIndustries(liveRef.current.projectId);
              saveProjectsComparables(projectDataValue.get('id'), liveRef.current.comparablesIds);
            },
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    addValidateCallBacks(validate);
    addOnSaveCallback(handleSubmit);

    return () => {
      deleteValidateCallbacks(validate);
      removeOnSaveCallback(handleSubmit);
      revertChanges();
      revertProjectChanges();
    };
  }, []);

  const setComparables = useCallback(newComparables => {
    setComparableValue(previousValues =>
      previousValues.map((value, index) => (newComparables[index] ? newComparables[index].legalName.trim() : value)),
    );
    setComparablesData(previousData =>
      previousData.map((item, index) => (newComparables[index] ? newComparables[index] : item)),
    );
    setComparablesIds(previousIds =>
      previousIds.map((id, index) => (newComparables[index] ? newComparables[index].id : id)),
    );
  }, []);

  const hasMountedRef = useRef(false);
  const previousComparablesRef = useRef(comparables);

  useEffect(() => {
    if (hasMountedRef.current) {
      if (comparables !== previousComparablesRef.current) {
        setComparables(comparables);
      }
    } else {
      hasMountedRef.current = true;
    }

    previousComparablesRef.current = comparables;
  }, [comparables, setComparables]);

  useEffect(() => {
    const afterSuccess = ({ response }) => {
      if (
        (type === TYPE_FINANCIAL && response.data.length > 0) ||
        (type === TYPE_STRATEGIC && response.data.buyerAddonProjects.length > 0)
      ) {
        const { id } = type === TYPE_FINANCIAL ? response.data[0] : response.data.buyerAddonProjects[0];

        loadProjectsComparables(id);
        loadIndustries(id);
        loadTemplates(id);
        loadProject(id);
        loadProjectCriteria(companyId, id);
      }
    };

    if (type === TYPE_FINANCIAL) {
      getBuyerProjectsPlatform({
        companyId,
        afterSuccess,
      });
    }

    if (type === TYPE_STRATEGIC) {
      getStrategicBuyerProjects({
        companyId,
        afterSuccess,
      });
    }
  }, [
    type,
    companyId,
    getBuyerProjectsPlatform,
    getStrategicBuyerProjects,
    loadProjectsComparables,
    loadIndustries,
    loadTemplates,
    loadProject,
    loadProjectCriteria,
  ]);

  const handleErrorClose = useCallback(
    (event, field) => {
      if (field === 'selectedBICs') {
        setIndustryCategories('');
        setBusinessModels('');
      }
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const loadProjectData = useCallback(
    id => {
      loadProjectsComparables(id);
      loadIndustries(id);
      loadTemplates(id);
      loadProject(id);
      loadProjectCriteria(companyId, id);
    },
    [loadProjectsComparables, loadIndustries, loadTemplates, loadProject, loadProjectCriteria, companyId],
  );

  const handleChangeProject = useCallback(
    event => {
      const newProjectId = event.target.value;

      clearComparables();
      setProjectId(newProjectId);
      setComparableValue(['', '', '', '', '']);
      setComparablesData([{}, {}, {}, {}, {}]);
      setComparablesIds(['', '', '', '', '']);
      loadProjectData(newProjectId);
    },
    [clearComparables, loadProjectData],
  );

  const handleDetailAdd = useCallback(
    event => {
      event.stopPropagation();

      const tags = getIn(common, 'selectedBICs');
      const models = getIn(common, 'selectedBMs');

      openPopup('TagsManagementPopup', {
        forBuyer: true,
        clientView: true,
        selectedTags: tags.reduce((accumulator, tag) => {
          accumulator[tag] = true;

          return accumulator;
        }, {}),
        selectedModels: models.reduce((accumulator, model) => {
          accumulator[model] = true;

          return accumulator;
        }, {}),
        callback: (tagSelected, modelSelected) => updateIndustryTags(tagSelected, modelSelected),
      });
    },
    [common, openPopup, updateIndustryTags],
  );

  const handleDetailEdit = useCallback(
    (event, detailName, detailIndex) => {
      startEditDetailEntity({ name: detailName, index: detailIndex });
    },
    [startEditDetailEntity],
  );

  const handleDetailSubmit = useCallback(
    (event, detailName) => {
      const base = criteria.get(detailName);

      if (!isDeepChanged(base)) return;
      if (!criteria.get(`${detailName}IsValid`, true)) return;

      const body = toJS(base);

      body.forEach(entity => {
        Object.keys(entity).forEach(key => {
          if (entity[key] instanceof moment) {
            try {
              entity[key] = entity[key].format('YYYY-MM-DD');
            } catch (error) {
              console.error("Can't transform ", key, entity[key], error);
            }
          }
        });
      });
      saveCompanyDetailEntity({
        companyId,
        name: detailName,
        body,
        realBody: prepareDetail(detailName, body, user.get('id')),
      });
    },
    [criteria, companyId, saveCompanyDetailEntity, user],
  );

  const detailsCallbacks = useMemo(
    () => ({
      onAdd: handleDetailAdd,
      onClick: handleDetailEdit,
      onSubmit: handleDetailSubmit,
    }),
    [handleDetailAdd, handleDetailEdit, handleDetailSubmit],
  );

  const handleFormChange = useCallback(
    ({ target: { name, type: inputType, value, checked } }) => {
      if (inputType === 'checkbox') return handleChange({ name, value: checked });

      return handleChange({ name, value });
    },
    [handleChange],
  );

  const handleProjectFormChange = useCallback(
    ({ target: { name, type: inputType, value, checked } }) => {
      if (inputType === 'checkbox') return handleChangeCommon({ name, value: checked });

      return handleChangeCommon({ name, value });
    },
    [handleChangeCommon],
  );

  const fetchSuggestions = useCallback(
    (value, index) => {
      const updatedValues = liveRef.current.comparableValue;

      updatedValues[index - 1] = value.trim();
      setComparableValue(updatedValues);
      setComparablesLoadable(true);
      findComparables(value);
    },
    [findComparables],
  );
  const handleGetSuggestion = useMemo(() => debounce(fetchSuggestions, 500), [fetchSuggestions]);

  const handleUpdateSuggestions = useCallback(
    ({ value }, index) => {
      if (!List.isList(value)) {
        const updatedValues = comparableValue;

        updatedValues[index - 1] = value;

        setComparableValue(updatedValues);
        setComparablesLoadable(true);
        setCanLoadComparables(true);
      }
    },
    [comparableValue],
  );

  const handleSuggestSelectCallback = useCallback(
    (data, index) => {
      const updatedValues = comparableValue;
      const updatedData = comparablesData;
      const updatedIds = comparablesIds;

      updatedValues[index - 1] = data.legalName.trim();
      updatedData[index - 1] = data;
      updatedIds[index - 1] = data.id;

      setComparableValue(updatedValues);
      setComparablesLoadable(false);
      setCanLoadComparables(false);
      setComparablesData(updatedData);
      setComparablesIds(updatedIds);

      handleChange({ name: 'comparablesIds', value: updatedIds });
      changeComparables(updatedIds, updatedData);
    },
    [comparableValue, comparablesData, comparablesIds, handleChange, changeComparables],
  );

  const searchFieldFocus = useCallback(
    index => {
      if (comparableValue[index]) {
        setCanLoadComparables(false);
        setComparablesLoadable(false);
      } else {
        setCanLoadComparables(true);
        setComparablesLoadable(true);
      }
    },
    [comparableValue],
  );

  const searchFieldFocusLeave = useCallback(
    index => {
      if (Object.keys(comparablesData[index]).length) {
        setCanLoadComparables(false);
        setComparablesLoadable(false);
      }
    },
    [comparablesData],
  );

  const menuStateChanged = useCallback(() => {
    setCanLoadComparables(false);
  }, []);

  const handleComparableDelete = useCallback(
    index => {
      const updatedValues = comparableValue.map((item, i) => (i === index ? '' : item));
      const updatedIds = comparablesIds.map((item, i) => (i === index ? '' : item));
      const updatedData = comparablesData.map((item, i) => (i === index ? {} : item));

      setComparableValue(updatedValues);
      setComparablesIds(updatedIds);
      setComparablesData(updatedData);

      handleChange({ name: 'comparablesIds', value: updatedIds });
      changeComparables(updatedIds, updatedData);
    },
    [comparableValue, comparablesIds, comparablesData, handleChange, changeComparables],
  );

  const getSuggestComparables = index => {
    const id = `projectComparablesId${index}`;
    const name = `comparablesSuggest${index}`;

    if (Object.keys(comparablesData[index - 1]).length) {
      return <span>{comparableValue[index - 1]}</span>;
    }

    return (
      <AutoCompleteContainer
        idName={id}
        idValue={criteria.get(id)}
        inputProps={{
          className: 'form-control',
          placeholder: 'Select Comparable',
          disabled: !canEditData,
        }}
        loadable={comparablesLoadable && canLoadComparables}
        onChange={value => handleUpdateSuggestions(value, index)}
        onFind={({ filter }) => {
          handleGetSuggestion(filter, index);
        }}
        onSelectCallback={data => handleSuggestSelectCallback(data, index)}
        render={suggestion => <span>{suggestion.legalName}</span>}
        suggestions={criteria.get('comparables')}
        suggestionsName="comparables"
        value={comparableValue[index - 1]}
        valueName={name}
      />
    );
  };

  if (toJS(projects).platform.isFetching) {
    return <></>;
  }

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <Criteria
        addOnProjects={addOnProjects}
        canEditData={canEditData}
        comparablesData={comparablesData}
        comparablesSuggest1={getSuggestComparables(1)}
        comparablesSuggest2={getSuggestComparables(2)}
        comparablesSuggest3={getSuggestComparables(3)}
        comparablesSuggest4={getSuggestComparables(4)}
        comparablesSuggest5={getSuggestComparables(5)}
        criteria={criteria}
        detailsCallbacks={detailsCallbacks}
        errorMessage={errorMessage}
        industries={industries}
        loadingProjectData={industryLoading || loading}
        menuStateChanged={menuStateChanged}
        onChange={handleFormChange}
        onChangeCommon={handleProjectFormChange}
        onChangeProject={handleChangeProject}
        onDelete={handleComparableDelete}
        onErrorClose={handleErrorClose}
        projectCommonData={common}
        projectData={projectData}
        projects={projects}
        searchFieldFocus={searchFieldFocus}
        searchFieldFocusLeave={searchFieldFocusLeave}
        showComparablesDeleteMenu={false}
        type={type}
      />
    </PrettyErrorProvider>
  );
};

CriteriaContainer.propTypes = {
  isDuplicateCompany: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    criteria: state.targetCompany.buyer.criteria,
    inputErrors: state.targetCompany.buyer.criteria.get('inputErrors'),
    projects: state.targetCompany.buyer.projects,
    addOnProjects: state.targetCompany.buyer.addOnProject.buyerProjects,
    common: state.project.common,
    industryLoading: state.project.common.get('industryLoading'),
    comparables: toJS(state.project.common.get('comparables')),
    industries: state.project.common.get('bic').concat(state.project.common.get('bm')),
    projectData: state.project.projectData,
    type: state.targetCompany.buyer.info.get('buyerType'),
    loading:
      state.targetCompany.buyer.projects.getIn(['addon', 'isFetching']) ||
      state.targetCompany.buyer.projects.getIn(['platform', 'isFetching']) ||
      state.project.common.get('templateLoading') ||
      state.project.common.get('projectLoading'),
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
    user: state.auth.get('user', null),
  };
}

export { CriteriaContainer };
export default connect(
  mapStateToProps,
  {
    handleUpdateCompany,
    startEditDetailEntity,
    saveInfo,
    closeValidationError,
    saveCompanyDetailEntity,
    saveBIC,
    findComparables,
    revertChanges,
    revertProjectChanges,
    getBuyerProjectsAddon,
    getBuyerProjectsPlatform,
    getStrategicBuyerProjects,
    loadIndustries,
    updateIndustryTags,
    updateProjectInfo,
    updateProjectCommonInfo,
    saveProject,
    saveIndustries,
    saveProjectsComparables,
    saveProjectCriteria,
    loadProjectsComparables,
    clearComparables,
    changeComparables,
    loadTemplates,
    loadProject,
    loadProjectCriteria,
  },
  mergeProps,
  connectOptions,
)(memo(CriteriaContainer));
